import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Box, Avatar, IconButton, Tooltip, Typography, Dialog, DialogTitle, DialogContent } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import IosShareIcon from "@mui/icons-material/IosShare";
import CloseIcon from "@mui/icons-material/Close";
import RedditIcon from "@mui/icons-material/Reddit";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import keaiRoundLogo from "../../assets/keai_violet.png";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import useChatEvents from "../hooks/useChatEvents";
import { useAuth0 } from "@auth0/auth0-react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import {
  vscDarkPlus,
  vs,
} from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { Message } from "../../store/slices/chatSlice";

interface ChatMessagesProps {
  // No props needed for this component
}

interface ThinkingContent {
  content: string;
  duration: number;
  originalText: string;
  cleanedText: string;
}

interface ThinkingPattern {
  name: string;
  regex: RegExp;
}

interface DebugState {
  debugMode: boolean;
  forceShowThinking: boolean;
}

// Interleaved message segments (text vs thinking)
type MessageSegment =
  | { kind: 'text'; content: string }
  | { kind: 'thinking'; content: string; duration: number };

// Helper to compute thinking duration
function computeThinkingDuration(content: string): number {
  const wordCount = (content || '')
    .split(/\s+/)
    .filter((w) => w.length > 0).length;
  const complexity = content?.includes('```') ? 1.5 : 1;
  const baseDuration = Math.min(Math.max(wordCount * 0.1, 2), 8) * complexity;
  return Math.round(baseDuration * 10) / 10;
}

// Remove all <thinking>...</thinking> sections
function stripAllThinkingTags(text: string): string {
  if (!text || typeof text !== 'string') return '';
  return text.replace(/<thinking>[\s\S]*?<\/thinking>/gi, '').trim();
}

// Parse interleaved segments preserving order: normal text and each <thinking> block
function parseThinkingSegments(text: string, isDebugMode: boolean = false): MessageSegment[] {
  if (!text || typeof text !== 'string') return [];

  const segments: MessageSegment[] = [];
  try {
    const openTag = '<thinking>';
    const closeTag = '</thinking>';
    let cursor = 0;

    while (cursor < text.length) {
      const openIdx = text.indexOf(openTag, cursor);
      if (openIdx === -1) {
        // No more thinking blocks; remainder is normal text
        const tail = text.slice(cursor);
        if (tail.length > 0) segments.push({ kind: 'text', content: tail });
        break;
      }

      // Preceding normal text
      if (openIdx > cursor) {
        const normal = text.slice(cursor, openIdx);
        if (normal.length > 0) segments.push({ kind: 'text', content: normal });
      }

      // Find closing tag (may be missing during streaming)
      const contentStart = openIdx + openTag.length;
      const closeIdx = text.indexOf(closeTag, contentStart);

      if (closeIdx === -1) {
        // Incomplete block: take everything to end as current thought content
        const thought = text.slice(contentStart).trim();
        if (thought.length > 0) {
          segments.push({
            kind: 'thinking',
            content: thought,
            duration: computeThinkingDuration(thought),
          });
        }
        break;
      } else {
        // Complete block
        const thought = text.slice(contentStart, closeIdx).trim();
        if (thought.length > 0) {
          segments.push({
            kind: 'thinking',
            content: thought,
            duration: computeThinkingDuration(thought),
          });
        }
        cursor = closeIdx + closeTag.length;
      }
    }

    if (isDebugMode) {
      console.log('[THINKING-SEGMENTS] Parsed segments:', segments.map(s => s.kind));
    }
  } catch (error) {
    console.error('[THINKING-ERROR] Failed to parse thinking segments:', error);
    // Fallback to a single text segment
    return [{ kind: 'text', content: text }];
  }

  return segments;
}

// Helper to remove research tags from displayed text
function removeResearchTags(text: string): string {
  if (!text || typeof text !== 'string') {
    return text;
  }
  // Remove <webresearch> and <deepresearch> tags (case-insensitive)
  return text
    .replace(/<webresearch>/gi, '')
    .replace(/<\/webresearch>/gi, '')
    .replace(/<deepresearch>/gi, '')
    .replace(/<\/deepresearch>/gi, '')
    .trim();
}

// Helper to detect Arabic text
function isArabicText(text: string): boolean {
  // Checks for Arabic Unicode block
  return /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(
    text
  );
}

// Content-based thinking content parser - model-agnostic
function parseThinkingContent(text: string, isDebugMode: boolean = false): ThinkingContent | null {
  // Only log in debug mode to reduce console noise
  if (isDebugMode) {
    console.log('[THINKING-DEBUG] parseThinkingContent called (content-based)');
    console.log('[THINKING-DEBUG] Input text length:', text?.length);
    console.log('[THINKING-DEBUG] Text preview:', text?.substring(0, 200) + '...');
    console.log('[THINKING-DEBUG] Debug mode:', isDebugMode);
  }
  
  // Debug mode: Force show test content for any model
  if (isDebugMode || text.toLowerCase().includes('test thinking')) {
    const debugResult = {
      content: 'DEBUG MODE: This is test thinking content for any model. Processing step by step: 1) Understanding the user\'s needs, 2) Analyzing available options, 3) Formulating the best response strategy.',
      duration: 3.5,
      originalText: text,
      cleanedText: text.replace(/test thinking/gi, 'Debug response generated')
    };
    if (isDebugMode) {
      console.log('[THINKING-DEBUG] Created debug thinking content:', debugResult);
    }
    return debugResult;
  }
  
  // Validate input text
  if (!text || typeof text !== 'string') {
    return null;
  }

  const patterns: ThinkingPattern[] = [
    // Primary format from backend: <thinking>content</thinking>
    { name: 'thinking-tags', regex: /<thinking>\s*([\s\S]*?)\s*<\/thinking>/gi },
    // Alternative tag often used by some models
    { name: 'think-tags', regex: /<think>\s*([\s\S]*?)\s*<\/think>/gi },
    // HTML comments: <!-- thinking -->content<!-- /thinking -->
    { name: 'html-comments', regex: /<!--\s*thinking\s*-->\s*([\s\S]*?)\s*<!--\s*\/thinking\s*-->/gi },
    // Chinese markers: [思考]content[/思考]
    { name: 'chinese-markers', regex: /\[思考\]\s*([\s\S]*?)\s*\[\/思考\]/gi },
    // Markdown-style thinking indicators (improved)
    { name: 'markdown-thinking', regex: /\*\*Thinking:\*\*\s*([\s\S]*?)(?=\n\s*\n|\*\*[^*]+\*\*|$)/gi },
    { name: 'markdown-chinese', regex: /\*\*思考：\*\*\s*([\s\S]*?)(?=\n\s*\n|\*\*[^*]+\*\*|$)/gi },
    { name: 'markdown-reasoning', regex: /\*\*Reasoning:\*\*\s*([\s\S]*?)(?=\n\s*\n|\*\*[^*]+\*\*|$)/gi },
    { name: 'markdown-reasoning-content', regex: /\*\*Reasoning Content:\*\*\s*([\s\S]*?)(?=\n\s*\n|\*\*[^*]+\*\*|$)/gi },
    // Alternative thinking markers (improved)
    { name: 'bracket-thinking', regex: /\[Thinking\]\s*([\s\S]*?)\s*\[\/Thinking\]/gi },
    { name: 'bracket-thinking-caps', regex: /\[THINKING\]\s*([\s\S]*?)\s*\[\/THINKING\]/gi },
    // Parentheses format: (thinking: content)
    { name: 'parentheses-thinking', regex: /\(thinking:\s*([\s\S]*?)\)/gi },
    // Curly braces format: {thinking: content}
    { name: 'curly-thinking', regex: /\{thinking:\s*([\s\S]*?)\}/gi },
  ];

  for (const pattern of patterns) {
    if (isDebugMode) {
      console.log('[THINKING-DEBUG] Testing pattern:', pattern.name);
    }
    
    const regex = new RegExp(pattern.regex.source, pattern.regex.flags);
    const match = regex.exec(text);
    
    if (match && match[1]) {
      const content = match[1].trim();
      
      if (isDebugMode) {
        console.log('[THINKING-DEBUG] Pattern', pattern.name, 'matched! Content length:', content.length);
        console.log('[THINKING-DEBUG] Raw matched content:', content.substring(0, 100) + '...');
      }
      
      if (content.length > 0) {
        try {
          // Calculate realistic thinking duration
          const wordCount = content.split(/\s+/).filter(word => word.length > 0).length;
          const complexity = content.includes('```') ? 1.5 : 1;
          const baseDuration = Math.min(Math.max(wordCount * 0.1, 2), 8) * complexity;
          const duration = Math.round(baseDuration * 10) / 10;

          // Create cleaned text without thinking content
          const cleanedText = text.replace(match[0], '').trim();

          const result = {
            content,
            duration,
            originalText: text,
            cleanedText: cleanedText || 'Processing your request...'
          };
          
          if (isDebugMode) {
            console.log('[THINKING-DEBUG] SUCCESS! Found thinking content via pattern:', pattern.name);
            console.log('[THINKING-DEBUG] Final result:', result);
          }
          
          return result;
        } catch (error) {
          console.error('[THINKING-ERROR] Error processing thinking content:', error);
          return null;
        }
      }
    } else if (isDebugMode) {
      console.log('[THINKING-DEBUG] Pattern', pattern.name, 'did not match');
    }
  }

  if (isDebugMode) {
    console.log('[THINKING-DEBUG] No thinking content patterns matched');
  }
  return null;
}

const ChatMessages: React.FC<ChatMessagesProps> = () => {
  const { messages } = useSelector((state: RootState) => state.chat);
  const activeChatId = useSelector(
    (state: RootState) => state.chatHistory.activeChatGroup
  );
  const selectedModel = useSelector((state: RootState) => state.model.selectedModel);
  const backgroundMode = useSelector((state: RootState) => state.background.mode);

  const isGenerating = useSelector((state: RootState) => state.chat.isGenerating);
  const isFileProcessing = useSelector((state: RootState) => state.fileProcessing.isFileProcessing);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  useChatEvents();
  const { user: auth0User } = useAuth0();
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareText, setShareText] = useState("");

  const prevMessagesLength = useRef(messages.length);
  const streamingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentChatIdRef = useRef<string | null>(null);
  const [debugMode, setDebugMode] = useState(false);
  const [forceShowThinking, setForceShowThinking] = useState(false);
  
  // Content-based thinking display - no longer model dependent
  // Thinking content will show based on message content, not selected model
  
  // Only log debug settings when debug mode is active
  if (debugMode) {
    console.log('[THINKING-MODEL] Current debug settings (model-agnostic):', {
      selectedModel,
      debugMode,
      forceShowThinking
    });
  }

  // Streaming logic for real-time message display
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [streamedText, setStreamedText] = useState("");
  const isExpectingMessageRef = useRef(false);
  const lastMessageTimestampRef = useRef<number>(0);

  // Prepare component to expect a message when generation starts
  useEffect(() => {
    if (isGenerating) {
      isExpectingMessageRef.current = true;
    }
  }, [isGenerating]);

  // 2. Check for new messages and stream if "armed".
  useEffect(() => {
    const hasNewMessages = messages.length > prevMessagesLength.current;

    if (messagesEndRef.current && hasNewMessages) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }

    // Only act on new messages
    if (hasNewMessages) {
      const newMessage = messages[messages.length - 1];

      // Log new messages only in development or when errors occur
      if (process.env.NODE_ENV === 'development' || debugMode) {
        console.log('New message detected:', {
          messageId: newMessage.messageId,
          source: newMessage.source,
          isHistorical: newMessage.isHistorical,
          isExpecting: isExpectingMessageRef.current,
          isGenerating,
          currentStreamingId: streamingMessageId
        });
      }

      // Check if this is a fresh message (arrived within last 5 seconds)
      const messageTime = newMessage.timestamp ? new Date(newMessage.timestamp).getTime() : Date.now();
      const isFreshMessage = messageTime > lastMessageTimestampRef.current;
      
      // Only stream fresh keai responses
      const shouldStream = (
        newMessage.source === "keai" &&
        !newMessage.isHistorical &&
        !streamingMessageId && // Don't start new stream if already streaming
        isFreshMessage && // Only stream fresh messages
        (isExpectingMessageRef.current || isGenerating || 
         (Date.now() - messageTime < 5000)) // Allow recent messages even if not explicitly expecting
      );
      
      // Update last message timestamp
      if (isFreshMessage) {
        lastMessageTimestampRef.current = messageTime;
      }
      
      if (debugMode) {
        console.log('Should stream:', shouldStream);
      }
      
      if (shouldStream) {
        isExpectingMessageRef.current = false; // Reset the flag
        if (debugMode) {
          console.log('Starting streaming for message:', newMessage.messageId);
        }

        if (streamingTimeoutRef.current) {
          clearTimeout(streamingTimeoutRef.current);
        }

        setStreamingMessageId(newMessage.messageId);
        setStreamedText("");

        // Remove research tags first, then strip all thinking tags for streaming
        const cleanedMessageText = removeResearchTags(newMessage.text);
        const textToStream = stripAllThinkingTags(cleanedMessageText) || cleanedMessageText;

        const words = textToStream
          .split(/(\s+|[.,!?;:])/g)
          .filter(Boolean);
        let wordIndex = 0;

        const animateStream = () => {
          if (wordIndex < words.length) {
            setStreamedText((prev) => prev + words[wordIndex]);
            wordIndex++;
            // Set delay for word-by-word streaming
            const delay = /^[.,!?;:]$/.test(words[wordIndex - 1])
              ? 30 // Longer pause for punctuation
              : 15; // Delay for words
            streamingTimeoutRef.current = setTimeout(animateStream, delay);
          } else {
            setStreamingMessageId(null);
            setStreamedText(textToStream); // Ensure final text is complete (without thinking tags)
          }
        };
        // Start streaming immediately to prevent flash
        streamingTimeoutRef.current = setTimeout(animateStream, 5);
      }
    }

    prevMessagesLength.current = messages.length;
  }, [messages]);

  // 3. Reset streaming state on chat switch.
  useEffect(() => {
    // Always reset streaming when chat changes (including from/to null)
    if (activeChatId !== currentChatIdRef.current) {
      if (debugMode) {
        console.log('Chat switched, stopping streaming:', {
          from: currentChatIdRef.current,
          to: activeChatId,
          wasStreaming: !!streamingMessageId
        });
      }
      
      if (streamingTimeoutRef.current) {
        clearTimeout(streamingTimeoutRef.current);
        streamingTimeoutRef.current = null;
      }
      setStreamingMessageId(null);
      setStreamedText("");
      isExpectingMessageRef.current = false;
      // Reset timestamp to prevent old messages from streaming when switching chats
      lastMessageTimestampRef.current = Date.now();
    }
    currentChatIdRef.current = activeChatId;
  }, [activeChatId]);

  // 4. Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (streamingTimeoutRef.current) {
        clearTimeout(streamingTimeoutRef.current);
      }
    };
  }, []);

  // --- End of Final Streaming Logic ---

  // Content-based thinking: No model-dependent state management needed
  // Thinking content displays immediately when detected in message content
  
  // Content persistence: Historical thinking content remains visible regardless of current model
  // No complex cleanup or auto-show logic needed - content drives display

  // Enhanced debug mode toggle with thinking content force-show
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'T') {
        setDebugMode(prev => {
          const newMode = !prev;
          console.log('[THINKING-DEBUG] Debug mode toggled:', newMode);
          if (newMode) {
            console.log('[THINKING-DEBUG] Debug mode activated - thinking content will be forced for all AI messages');
          }
          return newMode;
        });
      }
      if (e.ctrlKey && e.shiftKey && e.key === 'F') {
        setForceShowThinking(prev => {
          const newForce = !prev;
          console.log('[THINKING-DEBUG] Force show thinking toggled:', newForce);
          return newForce;
        });
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [debugMode, forceShowThinking]);

  // Enhanced Thinking Content Display Component with error handling
  const ThinkingContentDisplay: React.FC<{ content: string; duration: number }> = React.memo(({ content, duration }) => {
    if (debugMode) {
      console.log('[THINKING-DISPLAY] Rendering thinking content:', {
        contentLength: content?.length,
        duration,
        contentPreview: content?.substring(0, 50) + '...'
      });
    }

    // Validate props
    if (!content || typeof content !== 'string' || content.trim().length === 0) {
      return null;
    }

    // Sanitize duration
    const safeDuration = typeof duration === 'number' && !isNaN(duration) ? duration : 0;

    return (
      <Box
        sx={{
          mb: 2,
          p: 2.5,
          backgroundColor: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.08)' 
            : 'rgba(0, 0, 0, 0.03)',
          borderRadius: '12px',
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 1px 3px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.05)'
            : '0 1px 3px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.12)' 
              : 'rgba(0, 0, 0, 0.05)',
          }
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ 
            fontSize: '0.75rem',
            opacity: 0.7,
            fontStyle: 'italic',
            color: backgroundMode === "default" 
              ? '#ffffff'
              : theme.palette.text.secondary,
            display: 'block',
            mb: 1.5,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Thinking Process ({safeDuration}s)
        </Typography>
        <Typography
          sx={{
            opacity: 0.75,
            fontSize: '0.86rem',
            fontStyle: 'italic',
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
            color: backgroundMode === "default" 
              ? '#ffffff'
              : theme.palette.text.secondary,
            fontFamily: 'system-ui, -apple-system, sans-serif',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {content.trim()}
        </Typography>
      </Box>
    );
  });
  const copyCodeToClipboard = useCallback((code: string, codeId: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCodeId(codeId);
      setTimeout(() => setCopiedCodeId(null), 2000);
    });
  }, []);

  const copyMessageToClipboard = useCallback((text: string, messageId: string) => {
    // Remove research tags first, then strip all thinking tags before copying
    const cleanedText = removeResearchTags(text);
    const textToCopy = stripAllThinkingTags(cleanedText) || cleanedText;

    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    });
  }, []);

  const handleShareMessage = useCallback((text: string) => {
    // Remove research tags first, then strip all thinking tags before sharing
    const cleanedText = removeResearchTags(text);
    const textToShare = stripAllThinkingTags(cleanedText) || cleanedText;

    setShareText(textToShare);
    setShareModalOpen(true);
  }, []);

  const handleCloseShareModal = useCallback(() => {
    setShareModalOpen(false);
    setShareText("");
  }, []);

  const handleShareTo = useCallback(async (platform: string) => {
    const encodedText = encodeURIComponent(shareText);
    const title = encodeURIComponent("AI Response");
    
    let url = "";
    switch (platform) {
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodedText}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedText}`;
        break;
      case 'reddit':
        url = `https://reddit.com/submit?title=${title}&text=${encodedText}`;
        break;
      case 'native':
        if (navigator.share) {
          try {
            await navigator.share({
              title: 'AI Response',
              text: shareText,
            });
          } catch (error) {
            console.error('Native share failed:', error);
          }
        }
        break;
      case 'copy':
        try {
          await navigator.clipboard.writeText(shareText);
          console.log('Copied to clipboard');
        } catch (error) {
          console.error('Copy failed:', error);
        }
        break;
    }
    
    if (url) {
      window.open(url, '_blank', 'width=600,height=400');
    }
    
    handleCloseShareModal();
  }, [shareText, handleCloseShareModal]);

  const renderMessageContent = useCallback(
    (text: string, isAI: boolean, message: Message) => {
      // Clean research tags from all messages before processing
      const cleanedText = removeResearchTags(text);
      const isArabic = isAI && isArabicText(cleanedText);

      const isCurrentlyStreaming = message.messageId === streamingMessageId;

      // Interleaved segments for AI messages
      let segments: MessageSegment[] = [];
      let displayText = cleanedText;

      if (isAI) {
        try {
          segments = parseThinkingSegments(cleanedText, debugMode || forceShowThinking);
        } catch (error) {
          console.error('[THINKING-ERROR] Failed to parse thinking segments:', error);
          segments = [];
        }
      }

      // Handle display text based on streaming and thinking content
      if (isCurrentlyStreaming) {
        // During streaming, show partial content (already cleaned in streaming logic)
        displayText = streamedText;
        // Thinking segments are rendered interleaved below in the JSX using the streamed progress
      } else if (segments.length > 0) {
        // When not streaming and segments exist, display is handled per segment below
        // displayText is not used in that case
      }

      // Only log message processing in debug mode
      if (debugMode) {
        console.log('[THINKING-MESSAGE] Processing message (segments-based):', {
          messageId: message.messageId,
          isAI,
          segmentCount: segments.length,
          debugMode,
          forceShowThinking,
          selectedModel,
          textPreview: cleanedText?.substring(0, 100) + '...'
        });
      }

      if (isAI) {
        // Generate unique code block IDs for this message
        const codeBlockIds: string[] = [];
        const codeRegex = /```(?:\w+)?\s*[\s\S]*?```/g;
        let match;
        while ((match = codeRegex.exec(text)) !== null) {
          codeBlockIds.push(`code-${Math.random().toString(36).substring(2, 11)}`);
        }

        let codeBlockIndex = 0;

        const renderMarkdownContent = (md: string) => (
          <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkBreaks]}
                components={{
                  h1: ({ ...props }) => (
                    <h1
                      {...props}
                      style={{
                        fontSize: "2.5em",
                        fontWeight: 600,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        paddingBottom: "0.4em",
                        marginTop: "32px",
                        marginBottom: "20px",
                        color: backgroundMode === "default" 
                          ? '#ffffff'
                          : theme.palette.text.secondary,
                      }}
                    />
                  ),
                  h2: ({ ...props }) => (
                    <h2
                      {...props}
                      style={{
                        fontSize: "2em",
                        fontWeight: 600,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        paddingBottom: "0.4em",
                        marginTop: "32px",
                        marginBottom: "20px",
                        color: backgroundMode === "default" 
                          ? '#ffffff'
                          : theme.palette.text.secondary,
                      }}
                    />
                  ),
                  h3: ({ ...props }) => (
                    <>
                      <hr
                        style={{
                          border: `1px solid ${theme.palette.grey[800]}`,
                          margin: "16px 0",
                        }}
                      />
                      <h3
                        {...props}
                        style={{
                          fontSize: "1.7em",
                          fontWeight: 700,
                          marginBottom: "10px",
                          color: backgroundMode === "default" 
                            ? '#ffffff'
                            : theme.palette.text.secondary,
                        }}
                      />
                    </>
                  ),
                  h4: ({ ...props }) => (
                    <h4
                      {...props}
                      style={{
                        fontSize: "1.4em",
                        fontWeight: 600,
                        marginTop: "7px",
                        marginBottom: "7px",
                        color: backgroundMode === "default" 
                          ? '#ffffff'
                          : theme.palette.text.secondary,
                        }}
                      />
                    ),
                  p: ({ ...props }) => (
                    <p
                      {...props}
                      style={{
                        marginTop: "6px",
                        marginBottom: "6px",
                        color: backgroundMode === "default" 
                          ? '#ffffff'
                          : theme.palette.text.secondary,
                        fontSize: "1em",
                      }}
                    />
                  ),
                  ul: ({ ...props }) => (
                    <ul
                      {...props}
                      style={{
                        marginTop: "8px",
                        marginBottom: "16px",
                        color: backgroundMode === "default" 
                          ? '#ffffff'
                          : theme.palette.text.secondary,
                        paddingLeft: "1.5em",
                        listStyleType: "disc",
                      }}
                    />
                  ),
                  ol: ({ ...props }) => (
                    <ol
                      {...props}
                      style={{
                        marginTop: "8px",
                        marginBottom: "16px",
                        counterReset: "item",
                        color: backgroundMode === "default" 
                          ? '#ffffff'
                          : theme.palette.text.secondary,
                        }}
                      />
                    ),
                  li: ({ ...props }) => (
                    <li
                      {...props}
                      style={{
                        marginBottom: "8px",
                        color: backgroundMode === "default" 
                          ? '#ffffff'
                          : theme.palette.text.secondary,
                        paddingLeft: "0.5em",
                        position: "relative",
                        listStyleType: "disc",
                        display: "list-item",
                      }}
                    />
                  ),
                  a: ({ ...props }) => (
                    <a
                      {...props}
                      style={{
                        color: '#f472b6',
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                  blockquote: ({ ...props }) => (
                    <blockquote
                      {...props}
                      style={{
                        padding: "0 1em",
                        borderLeft: `0.25em solid ${theme.palette.divider}`,
                        color: backgroundMode === "default" 
                          ? '#ffffff'
                          : theme.palette.text.secondary,
                        margin: "16px 0",
                      }}
                    />
                  ),
                  strong: ({ ...props }) => (
                    <strong
                      {...props}
                      style={{
                        fontWeight: 600,
                        color: backgroundMode === "default" 
                          ? '#ffffff'
                          : theme.palette.text.secondary,
                      }}
                    />
                  ),
                  em: ({ ...props }) => (
                    <em
                      {...props}
                      style={{
                        fontStyle: "italic",
                        color: backgroundMode === "default" 
                          ? '#ffffff'
                          : theme.palette.text.secondary,
                        }}
                      />
                    ),
                  code({ className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || "");
                    const codeText = String(children).replace(/\n$/, "");

                    if (match) {
                      const codeId = codeBlockIds[codeBlockIndex++];
                      return (
                        <div style={{ position: "relative" }}>
                          <div
                            style={{
                              position: "absolute",
                              top: "7px",
                              right: "7px",
                              zIndex: 10,
                            }}
                          >
                            <Tooltip title="Copy code" placement="top">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  copyCodeToClipboard(codeText, codeId)
                                }
                                sx={{
                                  padding: "3px",
                                }}
                              >
                                {copiedCodeId === codeId ? (
                                  <CheckIcon fontSize="small" color="success" />
                                ) : (
                                  <ContentCopyIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </div>
                          <SyntaxHighlighter
                            style={
                              theme.palette.mode === "dark" ? vscDarkPlus : vs
                            }
                            language={match[1]}
                            PreTag="div"
                            customStyle={{
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "#111111"
                                  : "#f6f8fa",
                              borderRadius: "10px",
                              margin: "1rem 0",
                              padding: "1em 1em 1em 1em",
                              color: "#ffffff",
                            }}
                            {...props}
                          >
                            {codeText}
                          </SyntaxHighlighter>
                        </div>
                      );
                    }

                    return (
                      <code
                        className={className}
                        {...props}
                        style={{
                          fontFamily: '"Roboto Mono", monospace',
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#2d2d2d"
                              : "#f6f8fa",
                          padding: "0.2em 0.4em",
                          borderRadius: "3px",
                          fontSize: "85%",
                          color: backgroundMode === "default" 
                            ? "#000000"
                            : (theme.palette.mode === "dark" ? "#ffffff" : "#000000"),
                        }}
                      >
                        {children}
                      </code>
                    );
                  },
                  table: ({ ...props }) => (
                    <div style={{ overflow: "auto", margin: "16px 0" }}>
                      <table
                        {...props}
                        style={{
                          borderCollapse: "collapse",
                          width: "100%",
                          fontSize: "0.9em",
                          marginBottom: "1em",
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: "8px",
                        }}
                      />
                    </div>
                  ),
                  thead: ({ ...props }) => (
                    <thead
                      {...props}
                      style={{
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.02)",
                      }}
                    />
                  ),
                  tr: ({ ...props }) => (
                    <tr
                      {...props}
                      style={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    />
                  ),
                  th: ({ ...props }) => (
                    <th
                      {...props}
                      style={{
                        padding: "12px 16px",
                        textAlign: "left",
                        fontWeight: 600,
                        borderRight: `1px solid ${theme.palette.divider}`,
                      }}
                    />
                  ),
                  td: ({ ...props }) => (
                    <td
                      {...props}
                      style={{
                        padding: "12px 16px",
                        borderRight: `1px solid ${theme.palette.divider}`,
                        verticalAlign: "top",
                      }}
                    />
                  ),
                }}
          >
            {md}
          </ReactMarkdown>
        );

        // Helper: normalize text segments to align with stripAllThinkingTags() trimming during streaming
        const buildNormalizedSegments = (allSegments: MessageSegment[]): MessageSegment[] => {
          const firstTextIndex = allSegments.findIndex(s => s.kind === 'text');
          const lastTextIndex = (() => {
            for (let i = allSegments.length - 1; i >= 0; i -= 1) {
              if (allSegments[i].kind === 'text') return i;
            }
            return -1;
          })();
          return allSegments.map((seg, idx) => {
            if (seg.kind !== 'text') return seg;
            let content = seg.content;
            if (idx === firstTextIndex) content = content.replace(/^\s+/, '');
            if (idx === lastTextIndex) content = content.replace(/\s+$/, '');
            return { ...seg, content };
          });
        };

        // Helper: render interleaved streaming respecting original order of segments
        const renderInterleavedStreaming = (
          allSegments: MessageSegment[],
          streamed: string
        ) => {
          const normalized = buildNormalizedSegments(allSegments);
          const nodes: React.ReactNode[] = [];
          // Total streamed characters across text-only content
          const totalStreamed = streamed.length;

          // Track how many characters of text-only content we've traversed so far
          let traversedTextChars = 0;

          normalized.forEach((seg, idx) => {
            if (seg.kind === 'text') {
              const full = seg.content || '';
              const remainingFromHere = Math.max(totalStreamed - traversedTextChars, 0);
              const visibleLen = Math.max(Math.min(remainingFromHere, full.length), 0);
              const visible = visibleLen > 0 ? full.slice(0, visibleLen) : '';
              if (visible) {
                nodes.push(
                  <div key={`text-${message.messageId}-${idx}`}>
                    {renderMarkdownContent(visible)}
                  </div>
                );
              }
              traversedTextChars += full.length;
            } else {
              // Show thinking block only after all preceding text has fully streamed
              if (totalStreamed >= traversedTextChars) {
                nodes.push(
                  <ThinkingContentDisplay
                    key={`thinking-${message.messageId}-${idx}`}
                    content={seg.content}
                    duration={seg.duration}
                  />
                );
              }
            }
          });

          return nodes;
        };

        return (
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{ width: "100%" }}
              className="markdown-body"
              dir={isArabic ? "rtl" : "ltr"}
            >
              <div
                style={{ width: "100%", textAlign: isArabic ? "right" : "left" }}
              >
                {isCurrentlyStreaming ? (
                  renderInterleavedStreaming(segments, displayText)
                ) : (
                  segments.length > 0 ? (
                    segments.map((seg, idx) => (
                      seg.kind === 'thinking' ? (
                        <ThinkingContentDisplay
                          key={`thinking-${message.messageId}-${idx}`}
                          content={seg.content}
                          duration={seg.duration}
                        />
                      ) : (
                        <div key={`text-${message.messageId}-${idx}`}>
                          {renderMarkdownContent(seg.content)}
                        </div>
                      )
                    ))
                  ) : (
                    renderMarkdownContent(displayText)
                  )
                )}
              </div>
            </Box>
          </Box>
        );
      } else {
        // User message
        return (
          <div
            style={{
              borderRadius: "0.4rem",
              padding: "0",
              maxWidth: "100%",
              wordBreak: "break-word",
              width: "100%",
            }}
          >
            <div
              style={{
                fontFamily: "Roboto, sans-serif",
                fontSize: "1em",
                margin: 0,
                padding: 0,
                width: "100%",
                lineHeight: 1.8,
                whiteSpace: "pre-wrap",
                textAlign: "right",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                color: backgroundMode === "default" 
                  ? '#ffffff'
                  : theme.palette.text.secondary,
              }}
            >
              {displayText}
            </div>
          </div>
        );
      }
    },
    [streamingMessageId, streamedText, theme.palette, copyCodeToClipboard, debugMode, forceShowThinking, selectedModel, backgroundMode]
  );


  // Modify the message rendering to include timestamps
  const memoizedMessages = useMemo(
    () =>
      messages.map((message, index) => {
        const isAI = message.source === "keai";
        const isArabic = isAI && isArabicText(message.text);
        return (
          <div
            key={`message-${index}-${message.source}`}
            style={{
              width: "100%",
              marginBottom: "1.5rem",
              boxSizing: "border-box",
              backgroundColor: "transparent",
              padding: "16px 12px",
              display: "flex",
              justifyContent:
                isAI && isArabic
                  ? "flex-end"
                  : message.source === "user"
                  ? "flex-end"
                  : "flex-start",
            }}
          >
            <div
              style={{
                width: "100%",
                display: "flex",
                gap: "1rem",
                flexDirection: isAI && isArabic ? "row-reverse" : "row",
                justifyContent:
                  isAI && isArabic
                    ? "flex-end"
                    : message.source === "user"
                    ? "flex-end"
                    : "flex-start",
                alignItems: "flex-start",
              }}
            >
              {message.source !== "user" && (
                <Avatar
                  src={keaiRoundLogo}
                  alt="Keai logo"
                  sx={{
                    width: 36,
                    height: 36,
                    transition: "transform 0.3s ease",
                    transform: "scaleX(-1)",
                    flexShrink: 0,
                    order: 0,
                  }}
                />
              )}
              <div
                style={{
                  flexGrow: 0,
                  maxWidth: "80%",
                  minWidth: "120px",
                  padding: message.source === "user" ? "4px 0px" : "0",
                  borderRadius: message.source === "user" ? "12px" : "0",
                  order: message.source === "user" ? 0 : 1,
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems:
                    isAI && isArabic
                      ? "flex-end"
                      : message.source === "user"
                      ? "flex-end"
                      : "flex-start",
                  marginLeft:
                    message.source === "user" || (isAI && isArabic)
                      ? "auto"
                      : "0",
                  marginRight: isAI && isArabic ? 0 : undefined,
                }}
              >
                {renderMessageContent(message.text, isAI, message)}

                {/* Timestamp display */}
                {message.timestamp && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.65rem",
                      color: theme.palette.text.disabled,
                      opacity: 0.6,
                      marginTop: "4px",
                      alignSelf:
                        isAI && isArabic
                          ? "flex-end"
                          : message.source === "user"
                          ? "flex-end"
                          : "flex-start",
                    }}
                  >
                    {new Date(message.timestamp).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </Typography>
                )}

                 {message.source === "keai" && message.messageId !== streamingMessageId && (
                  <div
                    style={{
                      marginTop: "2px",
                      right:
                        (isAI && isArabic)
                          ? 0
                          : "auto",
                      left: message.source === "keai" && !isArabic ? 0 : "auto",
                      color: backgroundMode === "default"
                        ? '#ffffff'
                        : "text.secondary",
                      fontSize: "1.2rem",
                      whiteSpace: "nowrap",
                      display: "flex",
                      gap: "7px",
                      alignItems: "center",
                    }}
                  >
                    <Tooltip title={copiedMessageId === message.messageId ? "Copied!" : "Copy"}>
                      <IconButton
                        size="small"
                        onClick={() => copyMessageToClipboard(message.text, message.messageId)}
                        sx={{
                          padding: "3px",
                          color: backgroundMode === "default" 
                            ? '#ffffff'
                            : "text.secondary",
                        }}
                      >
                        {copiedMessageId === message.messageId ? (
                          <CheckIcon fontSize="inherit" sx={{ color: "#4caf50" }} />
                        ) : (
                          <ContentCopyIcon fontSize="inherit" />
                        )}
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Share message">
                      <IconButton
                        size="small"
                        onClick={() => handleShareMessage(message.text)}
                        sx={{
                          padding: "3px",
                          color: backgroundMode === "default" 
                            ? '#ffffff'
                            : "text.secondary",
                        }}
                      >
                        <IosShareIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    
                  </div>
                )}                
              </div>
              {message.source === "user" && (
                <Avatar
                  src={auth0User?.picture}
                  alt="User avatar"
                  sx={{
                    width: 28,
                    height: 28,
                    color: "#fff",
                    transition: "transform 0.3s ease",
                    flexShrink: 0,
                    order: 1,
                    marginTop: "3px",
                    alignSelf: "flex-start",
                  }}
                >
                  {auth0User?.name?.charAt(0).toUpperCase()}
                </Avatar>
              )}
            </div>
          </div>
        );
      }),
    [messages, auth0User, renderMessageContent, copiedMessageId, streamingMessageId, handleShareMessage, shareModalOpen, backgroundMode]
  );

  return (
    <Box
      ref={chatContainerRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        overflow: "auto",
        height: "100vh",
        width: "100%",
        padding: "1rem 5rem",
        boxSizing: "border-box",
        border: "none",
        boxShadow: "none",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        overflowY: "auto",
        paddingBottom: "150px",
      }}
    >
      {/* Debug Info Panel */}
      {debugMode && (
        <Box
          sx={{
            position: 'fixed',
            top: 10,
            right: 10,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
            p: 2,
            maxWidth: '300px',
            fontSize: '0.75rem',
            zIndex: 1000,
            backdropFilter: 'blur(10px)',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            THINKING DEBUG PANEL
          </Typography>
          <br />
          <Typography variant="caption" sx={{ 
            color: backgroundMode === "default" 
              ? '#ffffff'
              : 'text.secondary' 
          }}>
            Shortcuts:<br />
            • Ctrl+Shift+T: Toggle debug mode<br />
            • Ctrl+Shift+F: Force show thinking<br />
            <br />
            Status:<br />
            • Debug Mode: {debugMode ? 'ON' : 'OFF'}<br />
            • Force Show: {forceShowThinking ? 'ON' : 'OFF'}<br />
            • Current Model: {selectedModel || 'none'}<br />
            • Total Messages: {messages.length}<br />
            • AI Messages: {messages.filter(m => m.source === 'keai').length}<br />
            • Messages with Thinking: {messages.filter(m => m.source === 'keai' && parseThinkingContent(m.text, debugMode || forceShowThinking)).length}/{messages.filter(m => m.source === 'keai').length}<br />
          </Typography>
        </Box>
      )}

      {memoizedMessages}

      {/* Share Modal */}
      <Dialog 
        open={shareModalOpen} 
        onClose={handleCloseShareModal}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: theme.palette.mode === 'dark' ? '#2d2d2d' : '#ffffff',
            borderRadius: '16px',
            maxWidth: '600px',
            margin: '16px',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          px: 3,
          py: 2.5,
          pb: 1.5
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Share AI Response</Typography>
          <IconButton 
            onClick={handleCloseShareModal} 
            size="small"
            sx={{ 
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
            Preview:
          </Typography>
          <Box
            sx={{
              p: 3,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
              borderRadius: '12px',
              mb: 4,
              maxHeight: '200px',
              overflow: 'auto',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: 1.6,
                color: 'text.primary',
              }}
            >
              {shareText.length > 300 ? `${shareText.substring(0, 300)}...` : shareText}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontWeight: 500 }}>
            Share to:
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 4,
            justifyContent: 'center',
            alignItems: 'center',
            pb: 1,
          }}>
            <Box
              onClick={() => handleShareTo('twitter')}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                padding: 2,
                borderRadius: '12px',
                minWidth: 80,
                '&:hover': {
                  backgroundColor: 'rgba(29, 161, 242, 0.1)',
                }
              }}
            >
              <TwitterIcon sx={{ 
                fontSize: '32px', 
                color: '#1DA1F2',
                mb: 1
              }} />
              <Typography variant="caption" sx={{ 
                fontSize: '12px',
                fontWeight: 500,
                color: 'text.secondary'
              }}>
                Twitter
              </Typography>
            </Box>

            <Box
              onClick={() => handleShareTo('linkedin')}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                padding: 2,
                borderRadius: '12px',
                minWidth: 80,
                '&:hover': {
                  backgroundColor: 'rgba(0, 119, 181, 0.1)',
                }
              }}
            >
              <LinkedInIcon sx={{ 
                fontSize: '32px', 
                color: '#0077B5',
                mb: 1
              }} />
              <Typography variant="caption" sx={{ 
                fontSize: '12px',
                fontWeight: 500,
                color: 'text.secondary'
              }}>
                LinkedIn
              </Typography>
            </Box>

            <Box
              onClick={() => handleShareTo('reddit')}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                padding: 2,
                borderRadius: '12px',
                minWidth: 80,
                '&:hover': {
                  backgroundColor: 'rgba(255, 69, 0, 0.1)',
                }
              }}
            >
              <RedditIcon sx={{ 
                fontSize: '32px', 
                color: '#FF4500',
                mb: 1
              }} />
              <Typography variant="caption" sx={{ 
                fontSize: '12px',
                fontWeight: 500,
                color: 'text.secondary'
              }}>
                Reddit
              </Typography>
            </Box>

            <Box
              onClick={() => handleShareTo('native')}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
                padding: 2,
                borderRadius: '12px',
                minWidth: 80,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                }
              }}
            >
              <IosShareIcon sx={{ 
                fontSize: '32px', 
                color: 'text.secondary',
                mb: 1
              }} />
              <Typography variant="caption" sx={{ 
                fontSize: '12px',
                fontWeight: 500,
                color: 'text.secondary'
              }}>
                More
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>


      {isGenerating && (
        <div
          style={{
            width: "100%",
            marginBottom: "1.5rem",
            boxSizing: "border-box",
            backgroundColor: "transparent",
            padding: "16px 12px",
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          <div
            style={{
              width: "100%",
              display: "flex",
              gap: "1rem",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Avatar
              src={keaiRoundLogo}
              alt="Keai logo"
              sx={{
                width: 36,
                height: 36,
                transition: "transform 0.3s ease",
                transform: "scaleX(-1)",
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              <svg
                className="w-10"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 200 200"
              >
                <circle
                  fill="#FFFFFF"
                  stroke="#FFFFFF"
                  strokeWidth="15"
                  r="15"
                  cx="40"
                  cy="100"
                >
                  <animate
                    attributeName="opacity"
                    calcMode="spline"
                    dur="2"
                    values="1;0;1;"
                    keySplines=".5 0 .5 1;.5 0 .5 1"
                    repeatCount="indefinite"
                    begin="-.4"
                  ></animate>
                </circle>
                <circle
                  fill="#FFFFFF"
                  stroke="#FFFFFF"
                  strokeWidth="15"
                  r="15"
                  cx="100"
                  cy="100"
                >
                  <animate
                    attributeName="opacity"
                    calcMode="spline"
                    dur="2"
                    values="1;0;1;"
                    keySplines=".5 0 .5 1;.5 0 .5 1"
                    repeatCount="indefinite"
                    begin="-.2"
                  ></animate>
                </circle>
                <circle
                  fill="#FFFFFF"
                  stroke="#FFFFFF"
                  strokeWidth="15"
                  r="15"
                  cx="160"
                  cy="100"
                >
                  <animate
                    attributeName="opacity"
                    calcMode="spline"
                    dur="2"
                    values="1;0;1;"
                    keySplines=".5 0 .5 1;.5 0 .5 1"
                    repeatCount="indefinite"
                    begin="0"
                  ></animate>
                </circle>
              </svg>
            </Typography>
          </div>
        </div>
      )}

      {isFileProcessing && (
        <div
          style={{
            width: "100%",
            marginBottom: "1.5rem",
            boxSizing: "border-box",
            backgroundColor: "transparent",
            padding: "16px 12px",
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          <div
            style={{
              width: "100%",
              display: "flex",
              gap: "1rem",
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            }}
          >
            <Avatar
              src={keaiRoundLogo}
              alt="Keai logo"
              sx={{
                width: 36,
                height: 36,
                transition: "transform 0.3s ease",
                transform: "scaleX(-1)",
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              <svg
                className="w-10"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 200 200"
              >
                <circle
                  fill="#FFFFFF"
                  stroke="#FFFFFF"
                  strokeWidth="15"
                  r="15"
                  cx="40"
                  cy="100"
                >
                  <animate
                    attributeName="opacity"
                    calcMode="spline"
                    dur="2"
                    values="1;0;1;"
                    keySplines=".5 0 .5 1;.5 0 .5 1"
                    repeatCount="indefinite"
                    begin="-.4"
                  ></animate>
                </circle>
                <circle
                  fill="#FFFFFF"
                  stroke="#FFFFFF"
                  strokeWidth="15"
                  r="15"
                  cx="100"
                  cy="100"
                >
                  <animate
                    attributeName="opacity"
                    calcMode="spline"
                    dur="2"
                    values="1;0;1;"
                    keySplines=".5 0 .5 1;.5 0 .5 1"
                    repeatCount="indefinite"
                    begin="-.2"
                  ></animate>
                </circle>
                <circle
                  fill="#FFFFFF"
                  stroke="#FFFFFF"
                  strokeWidth="15"
                  r="15"
                  cx="160"
                  cy="100"
                >
                  <animate
                    attributeName="opacity"
                    calcMode="spline"
                    dur="2"
                    values="1;0;1;"
                    keySplines=".5 0 .5 1;.5 0 .5 1"
                    repeatCount="indefinite"
                    begin="0"
                  ></animate>
                </circle>
              </svg>
            </Typography>
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </Box>
  );
};

// Add error boundary wrapper
const ChatMessagesWithErrorBoundary: React.FC<ChatMessagesProps> = (props) => {
  return (
    <React.Suspense fallback={<div>Loading...</div>}>
      <ChatMessages {...props} />
    </React.Suspense>
  );
};

export default ChatMessagesWithErrorBoundary;
